/**
 * App layout shell — TopNav + main outlet, wrapped in WorkspaceProvider
 * so all protected pages share one workspace state.
 *
 * WorkspaceGate lives INSIDE the provider so routes can be exempted
 * (workspaces, settings) from the "must have a workspace" redirect.
 *
 * Also kicks an idle-time prefetch of the most-visited routes so the
 * user doesn't pay the chunk-fetch cost on their next click.
 */

import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import TopNav from "./TopNav";
import FloatingNav from "./FloatingNav";
import WorkspaceGate from "./WorkspaceGate";
import DownloadTray from "@/components/DownloadTray";
import NotificationPrompt from "@/components/NotificationPrompt";
import { WorkspaceProvider } from "@/hooks/useWorkspace";
import { prefetchCommonRoutes } from "@/lib/routePrefetch";
import { startPresenceHeartbeat } from "@/lib/pwa";

export default function AppLayout() {
  useEffect(() => {
    // Fire after first paint so we don't steal CPU from the initial render.
    const ric = (window as unknown as { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) ric(() => prefetchCommonRoutes(), { timeout: 2500 });
    else window.setTimeout(prefetchCommonRoutes, 1500);

    // Live-presence heartbeat — keeps pwa_devices.lastSeen fresh so the
    // Team page's "Live" pill reflects real activity. Ticks every 60s
    // while the tab is visible; pauses in the background.
    const stop = startPresenceHeartbeat(60_000);
    return stop;
  }, []);

  return (
    <WorkspaceProvider>
      <DownloadTray />
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg-page)",
      }}>
        <TopNav />
        <main
          className="cl-app-main"
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {/* WorkspaceGate decides whether to render the route or redirect
              new users to /workspaces to create their first one. */}
          <WorkspaceGate>
            <Outlet />
          </WorkspaceGate>
        </main>
        <FloatingNav />
        <NotificationPrompt />
      </div>
      {/* Bottom padding so page content clears the floating nav on mobile. */}
      <style>{`
        @media (max-width: 1023px) {
          .cl-app-main {
            padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 96px);
          }
        }
      `}</style>
    </WorkspaceProvider>
  );
}
